import { useUser } from "@clerk/react";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

type AdminModeProviderProps = {
  children: React.ReactNode;
  defaultValue?: boolean;
  storageKey?: string;
};

type AdminModeProviderState = {
  isAdminMode: boolean;
  setAdminMode: (enabled: boolean) => void;
  isGodModeEnabled: boolean;
  setGodModeEnabled: (enabled: boolean) => void;
  adminModeExpiresAt: number | null;
};

const ADMIN_MODE_TIMEOUT_MS = 10 * 60 * 1000;

const initialState: AdminModeProviderState = {
  isAdminMode: false,
  setAdminMode: () => null,
  isGodModeEnabled: false,
  setGodModeEnabled: () => null,
  adminModeExpiresAt: null,
};

export const AdminModeProviderContext =
  createContext<AdminModeProviderState>(initialState);

export function AdminModeProvider({
  children,
  defaultValue = false,
  storageKey = "admin-mode",
  ...props
}: AdminModeProviderProps) {
  const { user } = useUser();

  const buildKey = useCallback(
    (suffix: string) => `${user?.id}:${suffix}`,
    [user?.id],
  );

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [isAdminMode, setAdminMode] = useState<boolean>(defaultValue);
  const isAdminModeRef = useRef(isAdminMode);
  isAdminModeRef.current = isAdminMode;
  const [adminModeExpiresAt, setAdminModeExpiresAt] = useState<number | null>(null);
  const [isGodModeEnabled, setGodModeEnabled] = useState<boolean>(false);

  // Hydrate state from localStorage once user is available
  const hydrated = useRef(false);
  useEffect(() => {
    if (!user?.id || hydrated.current) return;
    hydrated.current = true;

    const completeKey = buildKey(storageKey);
    const expiresAtKey = buildKey("admin-mode-expires-at");
    const godModeKey = buildKey("god-mode");

    const storedGodMode = localStorage.getItem(godModeKey) === "true";
    setGodModeEnabled(storedGodMode);

    const storedAdmin = localStorage.getItem(completeKey) === "true" || defaultValue;
    if (storedAdmin) {
      if (storedGodMode) {
        // God mode: no timer, admin mode stays on indefinitely
        setAdminMode(true);
      } else {
        const expiresAt = Number(localStorage.getItem(expiresAtKey));
        if (expiresAt && Date.now() < expiresAt) {
          setAdminMode(true);
          setAdminModeExpiresAt(expiresAt);
        } else {
          localStorage.setItem(completeKey, "false");
          localStorage.removeItem(expiresAtKey);
          setAdminMode(false);
        }
      }
    }
  }, [user?.id, buildKey, storageKey, defaultValue]);

  const disableAdminMode = useCallback(() => {
    localStorage.setItem(buildKey(storageKey), "false");
    localStorage.removeItem(buildKey("admin-mode-expires-at"));
    localStorage.setItem(buildKey("god-mode"), "false");
    setAdminMode(false);
    setAdminModeExpiresAt(null);
    setGodModeEnabled(false);
  }, [buildKey, storageKey]);

  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    if (isAdminMode && adminModeExpiresAt && !isGodModeEnabled) {
      const remaining = adminModeExpiresAt - Date.now();
      if (remaining <= 0) {
        disableAdminMode();
      } else {
        timeoutRef.current = setTimeout(disableAdminMode, remaining);
      }
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [isAdminMode, adminModeExpiresAt, disableAdminMode, isGodModeEnabled]);

  // Sync admin mode state across tabs via storage events
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      const adminKey = buildKey(storageKey);
      const godKey = buildKey("god-mode");
      const expiresKey = buildKey("admin-mode-expires-at");

      if (e.key === adminKey) {
        if (e.newValue === "true") {
          const expiresAt = Number(localStorage.getItem(expiresKey));
          if (expiresAt && Date.now() < expiresAt) {
            setAdminMode(true);
            setAdminModeExpiresAt(expiresAt);
          }
        } else {
          setAdminMode(false);
          setAdminModeExpiresAt(null);
          setGodModeEnabled(false);
        }
      }

      if (e.key === godKey) {
        setGodModeEnabled(e.newValue === "true");
      }

      if (e.key === expiresKey && e.newValue) {
        setAdminModeExpiresAt(Number(e.newValue));
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, [buildKey, storageKey]);

  const handleSetAdminMode = useCallback(
    (enabled: boolean) => {
      if (enabled) {
        const expiresAt = Date.now() + ADMIN_MODE_TIMEOUT_MS;
        localStorage.setItem(buildKey(storageKey), "true");
        localStorage.setItem(buildKey("admin-mode-expires-at"), String(expiresAt));
        setAdminMode(true);
        setAdminModeExpiresAt(expiresAt);
      } else {
        disableAdminMode();
      }
    },
    [buildKey, storageKey, disableAdminMode],
  );

  const handleSetGodModeEnabled = useCallback(
    (enabled: boolean) => {
      localStorage.setItem(
        buildKey("god-mode"),
        enabled === true ? "true" : "false",
      );
      setGodModeEnabled(enabled);

      if (enabled) {
        // God mode: destroy the timer entirely
        localStorage.removeItem(buildKey("admin-mode-expires-at"));
        setAdminModeExpiresAt(null);
      } else if (isAdminModeRef.current) {
        // Leaving god mode while admin mode is still on: start a fresh timer
        const expiresAt = Date.now() + ADMIN_MODE_TIMEOUT_MS;
        localStorage.setItem(buildKey("admin-mode-expires-at"), String(expiresAt));
        setAdminModeExpiresAt(expiresAt);
      }
    },
    [buildKey],
  );

  const value = useMemo<AdminModeProviderState>(
    () => ({
      isAdminMode,
      setAdminMode: handleSetAdminMode,
      isGodModeEnabled,
      setGodModeEnabled: handleSetGodModeEnabled,
      adminModeExpiresAt,
    }),
    [isAdminMode, handleSetAdminMode, isGodModeEnabled, handleSetGodModeEnabled, adminModeExpiresAt],
  );

  return (
    <AdminModeProviderContext.Provider {...props} value={value}>
      {children}
    </AdminModeProviderContext.Provider>
  );
}

export const useAdminMode = () => {
  const context = useContext(AdminModeProviderContext);

  if (context === undefined)
    throw new Error("useAdmin must be used within a AdminModeProvider");

  return context;
};
